import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarMonthOutlined as CalendarIcon,
  CurrencyRupeeOutlined,
  ReceiptLongOutlined as ReceiptIcon,
  TrendingUpOutlined as TrendingUpIcon,
  DeleteOutlineOutlined as DeleteIcon,
  MoreHoriz as MoreIcon,
  RefreshOutlined as RefreshIcon,
  Search as SearchIcon,
  VisibilityOutlined as ViewIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import './RecordTable.css';

const money = (value) =>
  `₹ ${Number.parseFloat(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
};

const getCustomerName = (payment) => payment.customer || payment.customerName || '—';
const getAmount = (payment) => payment.amount || payment.amountReceived || 0;

export default function PaymentsList({
  payments = [],
  loading = false,
  onUpdatePayment,
  onDeletePayment,
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewingPayment, setViewingPayment] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPayment, setMenuPayment] = useState(null);
  const [dateAnchor, setDateAnchor] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filteredPayments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return payments;
    return payments.filter((payment) =>
      [
        getCustomerName(payment),
        payment.paymentNumber,
        payment.paymentMode,
        payment.depositTo,
        payment.referenceNumber,
        payment.invoiceNumber,
      ].some((value) => String(value || '').toLowerCase().includes(query))
    );
  }, [payments, searchQuery]);

  const totalReceived = useMemo(
    () => payments.reduce((sum, payment) => sum + Number(getAmount(payment)), 0),
    [payments]
  );

  const thisMonth = useMemo(() => {
    const now = new Date();
    return payments
      .filter((payment) => {
        if (!payment.paymentDate) return false;
        const date = new Date(`${payment.paymentDate}T00:00:00`);
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      })
      .reduce((sum, payment) => sum + Number(getAmount(payment)), 0);
  }, [payments]);

  const handleDelete = (payment) => {
    setMenuAnchor(null);
    setMenuPayment(null);
    if (window.confirm(`Delete payment ${payment.paymentNumber || ''}?`)) {
      onDeletePayment?.(payment.id);
    }
  };

  if (loading) {
    return (
      <Box className="payments-page">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={28} />
          <Typography sx={{ ml: 2 }}>Loading payments...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="payments-page">
      <Box className="payments-page-header">
        <Box className="payments-header-left">
          <Box className="payments-page-icon" aria-hidden="true">
            <CurrencyRupeeOutlined />
          </Box>
          <Box
            className="payments-page-title-wrap"
            style={{ cursor: 'pointer' }}
          >
            <h1 className="payments-title">Payments Received</h1>
            <p className="payments-subtitle">
              Track and manage payments received from your customers.
            </p>
          </Box>
        </Box>
        <Button
          className="payments-primary-btn"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/dashboard/payments/new')}
          sx={{ 
            flexShrink: 0,
            minWidth: 'fit-content'
          }}
        >
          Record Payment
        </Button>
      </Box>

      <Box className="payments-summary-grid">
        <Paper className="payment-summary-card payment-summary-card-total" elevation={0}>
          <Box className="payment-summary-icon"><ReceiptIcon /></Box>
          <Box className="payment-summary-body">
            <Typography>Total Payments</Typography>
            <strong>{payments.length}</strong>
            <span>Recorded transactions</span>
          </Box>
        </Paper>

        <Paper className="payment-summary-card payment-summary-card-received" elevation={0}>
          <Box className="payment-summary-icon"><CurrencyRupeeOutlined /></Box>
          <Box className="payment-summary-body">
            <Typography>Total Received</Typography>
            <strong>{money(totalReceived)}</strong>
            <span>Across all payment records</span>
          </Box>
        </Paper>

        <Paper className="payment-summary-card payment-summary-card-month" elevation={0}>
          <Box className="payment-summary-icon"><CalendarIcon /></Box>
          <Box className="payment-summary-body">
            <Typography>This Month</Typography>
            <strong>{money(thisMonth)}</strong>
            <span>Payments received this month</span>
          </Box>
        </Paper>

        <Paper className="payment-summary-card payment-summary-card-average" elevation={0}>
          <Box className="payment-summary-icon"><TrendingUpIcon /></Box>
          <Box className="payment-summary-body">
            <Typography>Average Payment</Typography>
            <strong>{money(payments.length ? totalReceived / payments.length : 0)}</strong>
            <span>Average transaction value</span>
          </Box>
        </Paper>
      </Box>

      {!filtersOpen ? (
        <button
          type="button"
          className="payments-show-filters-btn"
          onClick={() => setFiltersOpen(true)}
        >
          <SearchIcon className="payments-show-filters-icon" />
          <span>Show Filters</span>
        </button>
      ) : (
        <Paper className="payments-filter-card" elevation={0}>
          <Box className="payments-filter-header">
            <button
              type="button"
              className="payments-filter-title-btn"
              onClick={() => setFiltersOpen(false)}
              aria-label="Hide filters"
              title="Hide filters"
            >
              <Box className="payments-filter-icon"><SearchIcon /></Box>
              <Typography className="payments-filter-title">Filters</Typography>
              <ExpandLessIcon className="payments-filter-collapse-icon" />
            </button>
            <Button
              className="payments-filter-clear"
              onClick={() => setSearchQuery('')}
              disabled={!searchQuery}
            >
              Clear
            </Button>
          </Box>
          <Box className="payments-filter-controls">
            <TextField
              size="small"
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setPage(0);
              }}
              placeholder="Search payments"
              className="payments-filter-search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              className="payments-filter-btn"
              variant="outlined"
              startIcon={<CalendarIcon />}
              onClick={(event) => setDateAnchor(event.currentTarget)}
            >
              Date
            </Button>
            <Typography className="payments-filter-count">
              {filteredPayments.length} {filteredPayments.length === 1 ? 'payment' : 'payments'}
            </Typography>
            <IconButton
              className="payments-icon-btn"
              onClick={(event) => {
                setSearchQuery('');

                const icon = event.currentTarget.querySelector('.payments-refresh-icon');
                if (!icon || typeof icon.animate !== 'function') return;

                icon.getAnimations().forEach((animation) => animation.cancel());

                const animation = icon.animate(
                  [
                    { transform: 'rotate(0deg)' },
                    { transform: 'rotate(360deg)' },
                  ],
                  {
                    duration: 1000,
                    iterations: 1,
                    easing: 'linear',
                    fill: 'none',
                  }
                );

                animation.onfinish = () => {
                  icon.style.transform = '';
                };
              }}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshIcon
                fontSize="small"
                className="payments-refresh-icon"
              />
            </IconButton>
          </Box>
        </Paper>
      )}

      <Paper className="payments-table-card" elevation={0}>
        <Box className="payments-toolbar">
          <Box>
            <Typography className="payments-table-title">Payment History</Typography>
            <Typography className="payments-table-caption">All customer payment transactions</Typography>
          </Box>
        </Box>

        <TableContainer>
          <Table className="payments-table">
            <TableHead>
              <TableRow>
                <TableCell>PAYMENT #</TableCell>
                <TableCell>DATE</TableCell>
                <TableCell>CUSTOMER</TableCell>
                <TableCell>INVOICE #</TableCell>
                <TableCell>PAYMENT MODE</TableCell>
                <TableCell align="right">AMOUNT</TableCell>
                <TableCell align="right">BANK CHARGES</TableCell>
                <TableCell align="center">STATUS</TableCell>
                <TableCell align="center">ACTION</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="payments-empty-cell">
                    <Box className="payments-empty-state">
                      <Box className="payments-empty-icon"><ReceiptPlaceholder /></Box>
                      <Typography>No payment records found</Typography>
                      <span>
                        {searchQuery
                          ? 'Try a different search term.'
                          : 'Record your first customer payment to see it here.'}
                      </span>
                      {!searchQuery && (
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => navigate('/dashboard/payments/new')}
                        >
                          Record Payment
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayments
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((payment) => (
                    <TableRow hover key={payment.id}>
                      <TableCell>
                        <Typography className="payment-number">{payment.paymentNumber || '—'}</Typography>
                      </TableCell>
                      <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                      <TableCell>
                        <Typography className="customer-name">{getCustomerName(payment)}</Typography>
                        {payment.referenceNumber && (
                          <Typography className="reference-text">Ref: {payment.referenceNumber}</Typography>
                        )}
                      </TableCell>
                      <TableCell>{payment.invoiceNumber || '—'}</TableCell>
                      <TableCell>{payment.paymentMode || '—'}</TableCell>
                      <TableCell align="right" className="amount-cell">{money(getAmount(payment))}</TableCell>
                      <TableCell align="right">{money(payment.bankCharges)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={payment.status === 'VOID' ? 'Void' : 'Paid'}
                          size="small"
                          className={payment.status === 'VOID' ? 'void-chip' : 'paid-chip'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box className="payment-actions">
                          <IconButton size="small" onClick={() => setViewingPayment(payment)} title="View">
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              setMenuAnchor(event.currentTarget);
                              setMenuPayment(payment);
                            }}
                            title="More"
                          >
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredPayments.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          className="payments-pagination"
        />
      </Paper>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuPayment(null);
        }}
      >
        <MenuItem onClick={() => { setViewingPayment(menuPayment); setMenuAnchor(null); }}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} /> View details
        </MenuItem>
        <MenuItem onClick={() => handleDelete(menuPayment)} sx={{ color: '#DC2626' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete payment
        </MenuItem>
      </Menu>

      <Menu anchorEl={dateAnchor} open={Boolean(dateAnchor)} onClose={() => setDateAnchor(null)}>
        <MenuItem onClick={() => setDateAnchor(null)}>All dates</MenuItem>
        <MenuItem onClick={() => setDateAnchor(null)}>This month</MenuItem>
        <MenuItem onClick={() => setDateAnchor(null)}>Last month</MenuItem>
      </Menu>

      {viewingPayment && (
        <Box className="payment-modal-backdrop" onClick={() => setViewingPayment(null)}>
          <Paper className="payment-view-modal" elevation={0} onClick={(event) => event.stopPropagation()}>
            <Box className="payment-view-header">
              <Box>
                <Typography className="payment-view-eyebrow">Payment Received</Typography>
                <Typography className="payment-view-title">#{viewingPayment.paymentNumber || '—'}</Typography>
              </Box>
              <IconButton onClick={() => setViewingPayment(null)}><span aria-hidden="true">×</span></IconButton>
            </Box>
            <Box className="payment-view-amount">
              <span>Amount received</span>
              <strong>{money(getAmount(viewingPayment))}</strong>
            </Box>
              <Box className="payment-view-grid">
              <Detail label="Customer" value={getCustomerName(viewingPayment)} />
              <Detail label="Invoice #" value={viewingPayment.invoiceNumber || '—'} />
              <Detail label="Payment date" value={formatDate(viewingPayment.paymentDate)} />
              <Detail label="Payment mode" value={viewingPayment.paymentMode} />
              <Detail label="Reference #" value={viewingPayment.referenceNumber || '—'} />
              <Detail label="Bank Charges" value={money(viewingPayment.bankCharges)} />
              <Detail label="Notes" value={viewingPayment.notes || '—'} />
            </Box>
            <Box className="payment-view-footer">
              <Button variant="outlined" onClick={() => setViewingPayment(null)}>Close</Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

function Detail({ label, value }) {
  return (
    <Box className="payment-detail">
      <span>{label}</span>
      <strong>{value || '—'}</strong>
    </Box>
  );
}

function ReceiptPlaceholder() {
  return <span className="receipt-placeholder">₹</span>;
}
